import { useState } from 'react'
import { buildRenamedFilename, getRenameStemError, splitExtension } from 'citadel-shared/filename.js'

import axios from '../../dataClient/dataClient'

export const RENAME_GENERIC_ERROR = 'There was an error renaming the file. Please try again.'

const USER_FACING_STATUSES = [400, 409]

// Rename a file and normalize user-facing errors.
export async function renameFileRequest(file, filename) {
  try {
    const { data } = await axios.patch(`/file/${file.id}`, { filename })
    return data?.data ?? { ...file, filename }
  } catch (e) {
    console.error('Failed renaming file', file.id, e)
    const status = e?.response?.status
    const serverMessage = e?.response?.data?.message
    const error = new Error(USER_FACING_STATUSES.includes(status) && serverMessage ? serverMessage : RENAME_GENERIC_ERROR)
    error.status = status
    throw error
  }
}

// Shared state for dialog and inline rename controls.
export function useRenameFile(file) {
  const { extension } = splitExtension(file.filename)
  const [stem, setStemState] = useState(() => splitExtension(file.filename).stem)
  const [isSaving, setIsSaving] = useState(false)
  const [serverError, setServerError] = useState('')

  const candidate = buildRenamedFilename(stem, file.filename)
  const validationError = getRenameStemError(stem, file.filename)
  const unchanged = candidate === file.filename
  const errorText = serverError || validationError
  const canSubmit = !validationError && !isSaving

  function reset() {
    setStemState(splitExtension(file.filename).stem)
    setServerError('')
    setIsSaving(false)
  }

  function setStem(value) {
    setStemState(value)
    setServerError('')
  }

  async function submit() {
    if (!canSubmit) return undefined
    if (unchanged) return null
    setIsSaving(true)
    setServerError('')
    try {
      const updated = await renameFileRequest(file, candidate)
      setIsSaving(false)
      return updated
    } catch (e) {
      setIsSaving(false)
      setServerError(e.message)
      return undefined
    }
  }

  return { stem, setStem, extension, candidate, validationError, serverError, errorText, unchanged, isSaving, canSubmit, reset, submit }
}
